<template>
  <div class="tools-management">
    <el-tabs v-model="activeTab" @tab-change="handleTabChange">
      <el-tab-pane label="工具管理" name="tools">
        <div class="toolbar">
          <el-button type="primary" @click="showAddDialog">添加工具</el-button>
        </div>

    <div class="filters">
      <el-input
        v-model="searchKeyword"
        placeholder="搜索工具标题或内容..."
        class="search-input"
        clearable
        @input="handleSearch"
        style="width: 300px; margin-right: 15px;"
      >
        <template #prefix>
          <el-icon><Search /></el-icon>
        </template>
      </el-input>
      <el-select
        v-model="selectedCategory"
        placeholder="选择分类"
        clearable
        @change="handleFilterChange"
        style="width: 200px;"
      >
        <el-option
          v-for="cat in categories"
          :key="cat.id"
          :label="cat.name"
          :value="cat.id"
        />
      </el-select>
    </div>

    <el-table :data="displayItems" border v-loading="loading">
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="title" label="标题">
        <template #default="{ row }">
          <span style="display: flex; align-items: center; gap: 8px;">
            <el-tag v-if="row.is_pinned" type="warning" size="small">置顶</el-tag>
            <span>{{ row.title }}</span>
          </span>
        </template>
      </el-table-column>
      <el-table-column prop="category.name" label="分类">
        <template #default="{ row }">
          {{ row.category?.display_name || row.category?.name || '-' }}
        </template>
      </el-table-column>
      <el-table-column prop="is_pinned" label="置顶" width="100">
        <template #default="{ row }">
          <el-switch
            v-model="row.is_pinned"
            @change="togglePinned(row)"
            active-text="置顶"
            inactive-text=""
          />
        </template>
      </el-table-column>
      <el-table-column prop="view_count" label="浏览" width="100" />
      <el-table-column prop="is_active" label="启用" width="100">
        <template #default="{ row }">
          <el-switch
            v-model="row.is_active"
            @change="toggleStatus(row)"
            active-text="启用"
            inactive-text=""
          />
        </template>
      </el-table-column>
      <el-table-column prop="created_at" label="创建时间" width="180">
        <template #default="{ row }">
          {{ formatDate(row.created_at) }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="200">
        <template #default="{ row }">
          <el-button size="small" @click="editItem(row)">编辑</el-button>
          <el-button size="small" type="danger" @click="deleteItem(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      v-if="total > 0"
      :current-page="currentPage"
      :page-sizes="[20, 50, 100, { value: -1, label: '全部' }]"
      :page-size="pageSize"
      :total="total"
      layout="total, sizes, prev, pager, next, jumper"
      @size-change="handleSizeChange"
      @current-change="handlePageChange"
      class="pagination"
    />

    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="900px">
      <el-form :model="form" label-width="100px">
        <el-form-item label="分类">
          <el-select v-model="form.category_id">
            <el-option 
              v-for="cat in categories" 
              :key="cat.id" 
              :label="cat.display_name || cat.name" 
              :value="cat.id" 
            />
          </el-select>
        </el-form-item>
        <el-form-item label="标题">
          <el-input v-model="form.title" />
        </el-form-item>
        <el-form-item label="内容">
          <el-input v-model="form.content" type="textarea" :rows="15" />
        </el-form-item>
        <el-form-item label="封面图片">
          <el-upload
            ref="uploadRef"
            :auto-upload="false"
            :on-change="handleFileChange"
            :on-remove="handleFileRemove"
            :before-upload="beforeUpload"
            list-type="picture"
            :limit="1"
            accept="image/*"
          >
            <el-button type="primary">选择图片</el-button>
            <template #tip>
              <div class="el-upload__tip">支持 JPG/PNG 格式，建议尺寸 800x400，最大 5MB</div>
            </template>
          </el-upload>
          <div v-if="form.cover_image" class="file-info" style="margin-top: 10px; color: rgba(255, 255, 255, 0.8);">
            已选择: {{ form.cover_image.name }}
          </div>
          <div v-else-if="form.cover_image_url" style="margin-top: 10px;">
            <img :src="form.cover_image_url" style="max-width: 300px; max-height: 150px; border-radius: 4px;" />
          </div>
        </el-form-item>
        <el-form-item label="置顶">
          <el-switch v-model="form.is_pinned" active-text="置顶" inactive-text="" />
          <div class="form-tip">置顶的工具会优先显示在列表前面</div>
        </el-form-item>
        <el-form-item label="启用">
          <el-switch v-model="form.is_active" active-text="启用" inactive-text="" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveItem">保存</el-button>
      </template>
    </el-dialog>
      </el-tab-pane>
      
      <el-tab-pane label="分类管理" name="categories">
        <div class="toolbar">
          <el-button type="primary" @click="showCategoryDialog">添加分类</el-button>
        </div>
        
        <div class="category-list" v-loading="categoryLoading">
          <div
            v-for="cat in categories"
            :key="cat.id"
            class="category-item"
          >
            <div class="category-content">
              <div class="category-name">{{ cat.display_name || cat.name }}</div>
              <div class="category-description">{{ cat.description || '-' }}</div>
            </div>
            <div class="category-status">
              <el-switch
                v-model="cat.is_active"
                @change="toggleCategoryActive(cat)"
                active-text="启用"
                inactive-text="禁用"
              />
            </div>
            <div class="category-actions">
              <el-button size="small" @click="editCategory(cat)">编辑</el-button>
            </div>
          </div>
          <div v-if="categories.length === 0" class="empty-state">
            <el-empty description="暂无分类" />
          </div>
        </div>
        
        <el-dialog v-model="categoryDialogVisible" :title="categoryDialogTitle" width="600px">
          <el-form :model="categoryForm" label-width="100px">
            <el-form-item label="分类标识" required>
              <el-input 
                v-model="categoryForm.name" 
                :disabled="!!currentCategoryId"
                placeholder="请输入分类唯一标识（如：latest、development、opensource 等）"
              />
              <div class="form-tip" v-if="currentCategoryId">编辑时不能修改分类标识</div>
              <div class="form-tip">分类的唯一标识，用于API和代码中引用</div>
            </el-form-item>
            <el-form-item label="显示名称">
              <el-input 
                v-model="categoryForm.display_name" 
                placeholder="前台显示的名称（如：最新的工具、AI发展状况 等）"
              />
              <div class="form-tip">如果为空，则使用分类标识作为显示名称</div>
            </el-form-item>
            <el-form-item label="描述">
              <el-input v-model="categoryForm.description" type="textarea" :rows="4" />
            </el-form-item>
            <el-form-item label="启用">
              <el-switch v-model="categoryForm.is_active" active-text="启用" inactive-text="禁用" />
              <div class="form-tip">禁用后，该分类及其下的所有工具将不在前台显示</div>
            </el-form-item>
          </el-form>
          <template #footer>
            <el-button @click="categoryDialogVisible = false">取消</el-button>
            <el-button type="primary" @click="saveCategory">保存</el-button>
          </template>
        </el-dialog>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import api from '../../api'
import { Search } from '@element-plus/icons-vue'

export default {
  name: 'ToolsManagement',
  components: {
    Search
  },
  data() {
    return {
      items: [],
      categories: [],
      searchKeyword: '',
      selectedCategory: null,
      currentPage: 1,
      pageSize: 20,
      total: 0,
      loading: false,
      dialogVisible: false,
      dialogTitle: '添加工具',
      form: {
        category_id: null,
        title: '',
        content: '',
        cover_image: null,
        cover_image_url: null,
        is_active: true
      },
      currentId: null
    }
  },
    computed: {
      displayItems() {
        return this.items
      }
    },
  mounted() {
    this.loadItems()
    this.loadCategories()
  },
  methods: {
    async loadItems() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          page_size: this.pageSize === -1 ? 'all' : this.pageSize
        }
        
        // 添加分类筛选
        if (this.selectedCategory) {
          params.category = this.selectedCategory
        }
        
        // 添加搜索
        if (this.searchKeyword) {
          params.search = this.searchKeyword
        }
        
        const response = await api.get('/admin/tools/items/', { params })
        
        if (Array.isArray(response.data)) {
          this.items = response.data
          this.total = response.data.length
        } else {
          this.items = response.data.results || []
          this.total = response.data.count || 0
        }
      } catch (error) {
        this.$message.error('加载失败')
        this.items = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    async loadCategories() {
      try {
        const response = await api.get('/admin/tools/categories/')
        this.categories = Array.isArray(response.data) ? response.data : (response.data.results || [])
      } catch (error) {
        console.error('加载分类失败:', error)
        this.categories = []
      }
    },
    showAddDialog() {
      this.dialogTitle = '添加工具'
      this.currentId = null
      this.form = {
        category_id: null,
        title: '',
        content: '',
        cover_image: null,
        cover_image_url: null,
        is_pinned: false,
        is_active: true
      }
      if (this.$refs.uploadRef) {
        this.$refs.uploadRef.clearFiles()
      }
      this.dialogVisible = true
    },
    editItem(item) {
      this.dialogTitle = '编辑工具'
      this.currentId = item.id
      this.form = {
        category_id: item.category?.id || null,
        title: item.title,
        content: item.content,
        cover_image: null,
        cover_image_url: item.cover_image_url || null,
        is_pinned: item.is_pinned || false,
        is_active: item.is_active !== undefined ? item.is_active : true
      }
      if (this.$refs.uploadRef) {
        this.$refs.uploadRef.clearFiles()
      }
      this.dialogVisible = true
    },
    async deleteItem(item) {
      try {
        await this.$confirm('确定要删除吗？', '提示', { type: 'warning' })
        await api.delete(`/admin/tools/items/${item.id}/`)
        this.$message.success('删除成功')
        this.loadItems()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('删除失败')
        }
      }
    },
    async saveItem() {
      try {
        if (!this.form.title) {
          this.$message.warning('请输入标题')
          return
        }
        if (!this.form.category_id) {
          this.$message.warning('请选择分类')
          return
        }
        
        const formData = new FormData()
        formData.append('category_id', this.form.category_id)
        formData.append('title', this.form.title)
        formData.append('content', this.form.content || '')
        // 确保 is_pinned 和 is_active 是布尔值，FormData 会自动转换
        formData.append('is_pinned', this.form.is_pinned ? 'true' : 'false')
        formData.append('is_active', this.form.is_active ? 'true' : 'false')
        
        if (this.form.cover_image) {
          formData.append('cover_image', this.form.cover_image)
        }

        if (this.currentId) {
          await api.patch(`/admin/tools/items/${this.currentId}/`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
          this.$message.success('更新成功')
        } else {
          await api.post('/admin/tools/items/', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
          this.$message.success('添加成功')
        }
        this.dialogVisible = false
        // 清空表单和文件列表
        this.form.cover_image = null
        this.form.cover_image_url = null
        if (this.$refs.uploadRef) {
          this.$refs.uploadRef.clearFiles()
        }
        // 确保刷新列表
        await this.loadItems()
      } catch (error) {
        console.error('保存失败:', error)
        const errorMsg = error.response?.data?.detail || error.response?.data?.error || '保存失败'
        this.$message.error(errorMsg)
      }
    },
    handleFileChange(file) {
      this.form.cover_image = file.raw
      // 预览图片
      const reader = new FileReader()
      reader.onload = (e) => {
        this.form.cover_image_url = e.target.result
      }
      reader.readAsDataURL(file.raw)
    },
    handleFileRemove() {
      this.form.cover_image = null
      this.form.cover_image_url = null
    },
    beforeUpload(file) {
      // 验证文件类型
      const isImage = file.type.startsWith('image/')
      if (!isImage) {
        this.$message.error('只能上传图片文件')
        return false
      }
      // 验证文件大小（5MB）
      const isLt5M = file.size / 1024 / 1024 < 5
      if (!isLt5M) {
        this.$message.error('图片大小不能超过 5MB')
        return false
      }
      return true
    },
    handleSearch() {
      this.currentPage = 1
      this.loadItems()
    },
    handleFilterChange() {
      this.currentPage = 1
      this.loadItems()
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
      this.loadItems()
    },
    handlePageChange(page) {
      this.currentPage = page
      if (this.pageSize !== -1) {
        this.loadItems()
      }
    },
    async toggleStatus(item) {
      try {
        await api.patch(`/admin/tools/items/${item.id}/`, {
          is_active: item.is_active
        })
        this.$message.success(item.is_active ? '已启用' : '已禁用')
      } catch (error) {
        // 恢复原状态
        item.is_active = !item.is_active
        this.$message.error('状态更新失败')
        console.error(error)
      }
    },
    async togglePinned(item) {
      try {
        await api.patch(`/admin/tools/items/${item.id}/`, {
          is_pinned: item.is_pinned
        })
        this.$message.success(item.is_pinned ? '已置顶' : '已取消置顶')
        this.loadItems() // 重新加载以更新排序
      } catch (error) {
        // 恢复原状态
        item.is_pinned = !item.is_pinned
        this.$message.error('置顶状态更新失败')
        console.error(error)
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    },
    handleTabChange(tab) {
      if (tab === 'categories') {
        this.loadCategories()
      }
    },
    showCategoryDialog() {
      this.categoryDialogTitle = '添加分类'
      this.currentCategoryId = null
      this.categoryForm = {
        name: '',
        display_name: '',
        description: '',
        is_active: true
      }
      this.categoryDialogVisible = true
    },
    editCategory(cat) {
      this.categoryDialogTitle = '编辑分类'
      this.currentCategoryId = cat.id
      this.categoryForm = {
        name: cat.name,
        display_name: cat.display_name || '',
        description: cat.description || '',
        is_active: cat.is_active !== undefined ? cat.is_active : true
      }
      this.categoryDialogVisible = true
    },
    async saveCategory() {
      try {
        if (!this.categoryForm.name) {
          this.$message.warning('请选择分类名称')
          return
        }
        
        if (this.currentCategoryId) {
          await api.patch(`/admin/tools/categories/${this.currentCategoryId}/`, this.categoryForm)
          this.$message.success('更新成功')
        } else {
          await api.post('/admin/tools/categories/', this.categoryForm)
          this.$message.success('添加成功')
        }
        this.categoryDialogVisible = false
        this.loadCategories()
      } catch (error) {
        console.error('保存分类失败:', error)
        const errorMsg = error.response?.data?.detail || error.response?.data?.error || '保存失败'
        this.$message.error(errorMsg)
      }
    },
    async toggleCategoryActive(cat) {
      try {
        await api.patch(`/admin/tools/categories/${cat.id}/`, { is_active: cat.is_active })
        this.$message.success(cat.is_active ? '已启用' : '已禁用')
      } catch (error) {
        // 恢复原状态
        cat.is_active = !cat.is_active
        this.$message.error('操作失败')
        console.error(error)
      }
    }
  }
}
</script>

<style scoped>
.tools-management {
  width: 100%;
}

.toolbar {
  margin-bottom: 20px;
}

.filters {
  margin-bottom: 20px;
  display: flex;
  align-items: center;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.form-tip {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 5px;
}

.category-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.category-item {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 18px 20px;
  background: linear-gradient(135deg, rgba(20, 20, 30, 0.95), rgba(25, 25, 35, 0.9));
  border: 1px solid rgba(0, 150, 255, 0.3);
  border-radius: 10px;
  transition: all 0.3s ease;
}

.category-item:hover {
  border-color: rgba(0, 212, 255, 0.6);
  background: linear-gradient(135deg, rgba(0, 150, 255, 0.15), rgba(0, 212, 255, 0.1));
}

.category-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.category-name {
  font-size: 16px;
  font-weight: 600;
  color: #e0e0e0;
}

.category-description {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.5;
}

.category-status {
  display: flex;
  align-items: center;
  margin-right: 15px;
}

.category-actions {
  display: flex;
  gap: 10px;
}

.empty-state {
  padding: 40px;
  text-align: center;
}
</style>

